import type { Account, AccountLedgerEntry, BalanceAudit, Budget, CrDrEntry, Plan, PlanPayment, Transaction } from '../types';
import { buildBudgetUsage } from './budgets';
import { monthKey as monthKeyForDate, normalizeDateISO, todayISO } from './date';
import { buildAccountStatement, type StatementSummary } from './statement';

export type MonthlyCloseStatus = 'Not Reviewed' | 'In Progress' | 'Ready to Close' | 'Needs Attention';
export type ReconciliationStatus = 'Matched' | 'Mismatch' | 'Not Reconciled';
export type MonthlyCloseIssueLevel = 'critical' | 'warning' | 'info';

export type MonthlyCloseSource = {
  transactions: Transaction[];
  payments: PlanPayment[];
  plans: Plan[];
  crdr: CrDrEntry[];
  budgets: Budget[];
  accounts: Account[];
  accountLedger: AccountLedgerEntry[];
  balanceAudits: BalanceAudit[];
};

export type ReconciliationRow = {
  account: Account;
  statement: StatementSummary;
  actualBalance: number | null;
  difference: number | null;
  status: ReconciliationStatus;
  lastAudit?: BalanceAudit;
  history: BalanceAudit[];
};

export type CreditCardReconciliationRow = ReconciliationRow & {
  openingOutstanding: number;
  purchases: number;
  payments: number;
  closingOutstanding: number;
  creditLimit: number;
  availableCredit: number;
  utilizationPct: number;
  actualAvailableLimit: number | null;
  availableLimitDifference: number | null;
};

export type MonthlyCloseIssue = {
  id: string;
  level: MonthlyCloseIssueLevel;
  issue: string;
  count: number;
  amount?: number;
  action: string;
};

export type MonthlyCloseChecklistItem = {
  id: string;
  label: string;
  state: 'ok' | 'warning' | 'critical' | 'info';
  detail: string;
};

export type MonthlyCloseModel = {
  monthKey: string;
  fromDate: string;
  toDate: string;
  status: MonthlyCloseStatus;
  income: number;
  expense: number;
  investment: number;
  netCashFlow: number;
  openingLiquidBalance: number;
  closingLiquidBalance: number;
  transferTotal: number;
  cardPaymentTotal: number;
  closingCardOutstanding: number;
  duePlansTotal: number;
  paidPlansTotal: number;
  overduePlansTotal: number;
  duePlans: Plan[];
  paidPlans: PlanPayment[];
  unpaidPlans: Plan[];
  overduePlans: Plan[];
  pausedPlans: Plan[];
  accountRows: ReconciliationRow[];
  cardRows: CreditCardReconciliationRow[];
  issues: MonthlyCloseIssue[];
  criticalIssues: MonthlyCloseIssue[];
  warnings: MonthlyCloseIssue[];
  infoItems: MonthlyCloseIssue[];
  checklist: MonthlyCloseChecklistItem[];
  budgetTotal: number;
  budgetSpent: number;
  budgetRemaining: number;
  budgetExceeded: number;
  budgetWarning: number;
  crReceivable: number;
  drPayable: number;
  crdrSettled: number;
  crdrOpenItems: number;
};

const sum = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((total, row) => total + Number(pick(row) || 0), 0);

const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

export function resolveCloseMonthRange(inputMonthKey: string) {
  const normalized = /^\d{4}-\d{2}$/.test(inputMonthKey) ? inputMonthKey : monthKeyForDate(inputMonthKey || todayISO());
  const year = Number(normalized.slice(0, 4));
  const month = Number(normalized.slice(5, 7));
  const toDate = `${normalized}-${String(daysInMonth(year, month)).padStart(2, '0')}`;
  return { monthKey: normalized, fromDate: `${normalized}-01`, toDate };
}

const dateInRange = (date: string | undefined, from: string, to: string) => {
  const iso = normalizeDateISO(date);
  return iso >= from && iso <= to;
};

export function reconciliationStatus(actualBalance: number | null | undefined, systemBalance: number, tolerance = 0): ReconciliationStatus {
  if (actualBalance === null || actualBalance === undefined || Number.isNaN(Number(actualBalance))) return 'Not Reconciled';
  return Math.abs(Number(actualBalance) - Number(systemBalance)) <= tolerance ? 'Matched' : 'Mismatch';
}

const latestAuditForAccount = (audits: BalanceAudit[], accountId: string) =>
  audits
    .filter((audit) => audit.accountId === accountId)
    .sort((a, b) => normalizeDateISO(b.dateISO || b.auditDate).localeCompare(normalizeDateISO(a.dateISO || a.auditDate)) || String(b.id).localeCompare(String(a.id)))[0];

const groupLinkedRows = (ledger: AccountLedgerEntry[], field: 'transferId' | 'cardPaymentId') => {
  const groups = new Map<string, AccountLedgerEntry[]>();
  ledger.forEach((row) => {
    const key = String(row[field] || '');
    if (!key) return;
    groups.set(key, [...(groups.get(key) || []), row]);
  });
  return groups;
};

const brokenTransferCount = (ledger: AccountLedgerEntry[]) => Array.from(groupLinkedRows(ledger, 'transferId').values()).filter((rows) => {
  const debit = rows.find((row) => row.type === 'transfer_out' && row.direction === 'debit');
  const credit = rows.find((row) => row.type === 'transfer_in' && row.direction === 'credit');
  return rows.length !== 2 || !debit || !credit || debit.relatedAccountId !== credit.accountId || credit.relatedAccountId !== debit.accountId || Number(debit.amount || 0) !== Number(credit.amount || 0);
}).length;

const brokenCardPaymentCount = (ledger: AccountLedgerEntry[]) => Array.from(groupLinkedRows(ledger, 'cardPaymentId').values()).filter((rows) => {
  const debit = rows.find((row) => row.type === 'credit_card_payment' && row.direction === 'debit');
  const credit = rows.find((row) => row.type === 'credit_card_payment' && row.direction === 'credit');
  return rows.length !== 2 || !debit || !credit || debit.relatedAccountId !== credit.accountId || credit.relatedAccountId !== debit.accountId || Number(debit.amount || 0) !== Number(credit.amount || 0);
}).length;

const rowIssue = (id: string, level: MonthlyCloseIssueLevel, issue: string, count: number, action: string, amount?: number): MonthlyCloseIssue | undefined =>
  count > 0 ? { id, level, issue, count, amount, action } : undefined;

export function buildMonthlyCloseModel(source: MonthlyCloseSource, selectedMonthKey: string, now = todayISO()): MonthlyCloseModel {
  const { monthKey, fromDate, toDate } = resolveCloseMonthRange(selectedMonthKey);
  const transactions = source.transactions.filter((row) => row.status !== 'deleted' && row.active !== false);
  const rangeTx = transactions.filter((row) => dateInRange(row.dateISO || row.date, fromDate, toDate));
  const activeAccounts = source.accounts.filter((account) => account.status !== 'deleted' && (account.active !== false || source.accountLedger.some((row) => row.accountId === account.id)));
  const rangeLedger = source.accountLedger.filter((row) => dateInRange(row.dateISO || row.date, fromDate, toDate) && Number(row.amount || 0) !== 0);
  const rangeAudits = source.balanceAudits.filter((row) => dateInRange(row.dateISO || row.auditDate, fromDate, toDate));
  const duePlans = source.plans.filter((plan) => plan.status !== 'deleted' && plan.active !== false && dateInRange(plan.nextDueDate || plan.dueDate, fromDate, toDate));
  const paidPlans = source.payments.filter((payment) => dateInRange(payment.paidDate, fromDate, toDate));
  const paidPlanIds = new Set(paidPlans.map((payment) => payment.planId));
  const unpaidPlans = duePlans.filter((plan) => !paidPlanIds.has(plan.id));
  const overduePlans = duePlans.filter((plan) => normalizeDateISO(plan.nextDueDate || plan.dueDate) < normalizeDateISO(now) && !paidPlanIds.has(plan.id));
  const pausedPlans = source.plans.filter((plan) => plan.status === 'paused' && dateInRange(plan.nextDueDate || plan.dueDate, fromDate, toDate));
  const rangeCrdr = source.crdr.filter((row) => dateInRange(row.date, fromDate, toDate));
  const rangeBudgets = source.budgets.filter((budget) => budget.status !== 'deleted' && budget.active !== false && budget.monthKey === monthKey);

  const accountRows = activeAccounts
    .filter((account) => account.type !== 'credit_card')
    .map((account) => {
      const statement = buildAccountStatement(account, source.accountLedger.filter((row) => row.accountId === account.id && Number(row.amount || 0) !== 0), fromDate, toDate, 'oldest');
      const history = rangeAudits.filter((audit) => audit.accountId === account.id).sort((a, b) => normalizeDateISO(b.dateISO || b.auditDate).localeCompare(normalizeDateISO(a.dateISO || a.auditDate)));
      const lastAudit = latestAuditForAccount(rangeAudits, account.id);
      const actualBalance = lastAudit ? Number(lastAudit.actualBalance || 0) : null;
      const difference = actualBalance === null ? null : actualBalance - statement.closingBalance;
      return { account, statement, actualBalance, difference, status: reconciliationStatus(actualBalance, statement.closingBalance), lastAudit, history };
    });

  const cardRows = activeAccounts
    .filter((account) => account.type === 'credit_card')
    .map((account) => {
      const ledger = source.accountLedger.filter((row) => row.accountId === account.id && Number(row.amount || 0) !== 0);
      const statement = buildAccountStatement(account, ledger, fromDate, toDate, 'oldest');
      const monthRows = ledger.filter((row) => dateInRange(row.dateISO || row.date, fromDate, toDate));
      const beforeRows = ledger.filter((row) => normalizeDateISO(row.dateISO || row.date) < fromDate);
      const openingOutstanding = beforeRows.reduce((balance, row) => balance + (row.direction === 'debit' ? Number(row.amount || 0) : -Number(row.amount || 0)), Number(account.openingBalance || 0));
      const monthDebit = sum(monthRows.filter((row) => row.direction === 'debit'), (row) => row.amount);
      const monthCredit = sum(monthRows.filter((row) => row.direction === 'credit'), (row) => row.amount);
      const closingOutstanding = openingOutstanding + monthDebit - monthCredit;
      const history = rangeAudits.filter((audit) => audit.accountId === account.id).sort((a, b) => normalizeDateISO(b.dateISO || b.auditDate).localeCompare(normalizeDateISO(a.dateISO || a.auditDate)));
      const lastAudit = latestAuditForAccount(rangeAudits, account.id);
      const actualBalance = lastAudit ? Number(lastAudit.actualBalance || 0) : null;
      const difference = actualBalance === null ? null : actualBalance - closingOutstanding;
      const creditLimit = Number(account.creditLimit || 0);
      return {
        account,
        statement,
        actualBalance,
        difference,
        status: reconciliationStatus(actualBalance, closingOutstanding),
        lastAudit,
        history,
        openingOutstanding: Math.max(openingOutstanding, 0),
        purchases: sum(monthRows.filter((row) => row.type === 'credit_card_spend'), (row) => row.amount),
        payments: sum(monthRows.filter((row) => row.type === 'credit_card_payment' && row.direction === 'credit'), (row) => row.amount),
        closingOutstanding: Math.max(closingOutstanding, 0),
        creditLimit,
        availableCredit: Math.max(creditLimit - closingOutstanding, 0),
        utilizationPct: creditLimit > 0 ? Math.round((closingOutstanding / creditLimit) * 100) : 0,
        actualAvailableLimit: lastAudit && lastAudit.actualAvailableLimit !== undefined ? Number(lastAudit.actualAvailableLimit || 0) : null,
        availableLimitDifference: lastAudit && lastAudit.availableLimitDifference !== undefined ? Number(lastAudit.availableLimitDifference || 0) : null
      } satisfies CreditCardReconciliationRow;
    });

  const budgetUsage = buildBudgetUsage(rangeBudgets, rangeTx);
  const budgetTotal = sum(budgetUsage, (budget) => budget.limitAmount);
  const budgetSpent = sum(budgetUsage, (budget) => budget.spent);
  const accountIds = new Set(activeAccounts.map((account) => account.id));
  const unassigned = rangeTx.filter((row) => !row.accountId);
  const missingAccountTx = rangeTx.filter((row) => row.accountId && !accountIds.has(row.accountId));
  const noCategory = rangeTx.filter((row) => !String(row.category || '').trim());
  const negativeAccounts = accountRows.filter((row) => row.statement.closingBalance < 0);
  const cardMissingLimit = cardRows.filter((row) => row.creditLimit <= 0);
  const plansMissingAccount = duePlans.filter((plan) => !plan.paidFromAccountId);
  const brokenTransfers = brokenTransferCount(source.accountLedger);
  const brokenCards = brokenCardPaymentCount(source.accountLedger);

  const issues = [
    rowIssue('unreconciled-accounts', 'critical', 'Accounts not reconciled for selected month', accountRows.filter((row) => row.status === 'Not Reconciled').length + cardRows.filter((row) => row.status === 'Not Reconciled').length, 'Enter actual balance'),
    rowIssue('mismatch-accounts', 'critical', 'Balance mismatches need review', accountRows.filter((row) => row.status === 'Mismatch').length + cardRows.filter((row) => row.status === 'Mismatch').length, 'Open statement and review'),
    rowIssue('missing-account', 'critical', 'Transactions reference a missing account', missingAccountTx.length, 'Review transactions', sum(missingAccountTx, (row) => row.amount)),
    rowIssue('broken-transfer', 'critical', 'Broken transfer pairs detected', brokenTransfers, 'Review transfers'),
    rowIssue('broken-card-payment', 'critical', 'Broken credit-card payment pairs detected', brokenCards, 'Review card payments'),
    rowIssue('unassigned', 'warning', 'Transactions without account assignment', unassigned.length, 'Assign account in transactions', sum(unassigned, (row) => row.amount)),
    rowIssue('negative', 'warning', 'Liquid accounts closing negative', negativeAccounts.length, 'Open account statement'),
    rowIssue('overdue-plans', 'warning', 'Overdue unpaid commitments', overduePlans.length, 'Open Plans', sum(overduePlans, (plan) => plan.amount)),
    rowIssue('missing-plan-account', 'info', 'Plans missing paid-from account', plansMissingAccount.length, 'Update plans'),
    rowIssue('missing-card-limit', 'info', 'Credit cards missing credit limit', cardMissingLimit.length, 'Update cards'),
    rowIssue('missing-category', 'info', 'Transactions without category', noCategory.length, 'Review transactions'),
    rowIssue('budget-crossed', 'warning', 'Budget categories crossed 100%', budgetUsage.filter((budget) => budget.state === 'crossed').length, 'Review budgets'),
    rowIssue('budget-warning', 'info', 'Budget categories above 80%', budgetUsage.filter((budget) => budget.state === 'warning').length, 'Review budgets')
  ].filter(Boolean) as MonthlyCloseIssue[];

  const criticalIssues = issues.filter((issue) => issue.level === 'critical');
  const warnings = issues.filter((issue) => issue.level === 'warning');
  const infoItems = issues.filter((issue) => issue.level === 'info');
  const hasAudit = [...accountRows, ...cardRows].some((row) => row.lastAudit);
  const status: MonthlyCloseStatus = criticalIssues.length ? 'Needs Attention' : warnings.length ? 'In Progress' : hasAudit ? 'Ready to Close' : 'Not Reviewed';
  const checklist: MonthlyCloseChecklistItem[] = [
    { id: 'accounts', label: 'Bank/cash/wallet accounts reconciled', state: accountRows.some((row) => row.status !== 'Matched') ? 'critical' : 'ok', detail: `${accountRows.filter((row) => row.status === 'Matched').length}/${accountRows.length} matched` },
    { id: 'cards', label: 'Credit cards reconciled separately', state: cardRows.some((row) => row.status !== 'Matched') ? 'critical' : 'ok', detail: `${cardRows.filter((row) => row.status === 'Matched').length}/${cardRows.length} matched` },
    { id: 'linked', label: 'No broken linked ledger events', state: brokenTransfers || brokenCards ? 'critical' : 'ok', detail: `${brokenTransfers + brokenCards} broken pair(s)` },
    { id: 'unassigned', label: 'No critical unassigned or missing account rows', state: missingAccountTx.length ? 'critical' : unassigned.length ? 'warning' : 'ok', detail: `${unassigned.length} unassigned, ${missingAccountTx.length} missing account` },
    { id: 'plans', label: 'Commitments reviewed', state: overduePlans.length ? 'warning' : 'ok', detail: `${overduePlans.length} overdue, ${unpaidPlans.length} unpaid` },
    { id: 'budgets', label: 'Budget status reviewed', state: budgetUsage.some((budget) => budget.state === 'crossed') ? 'warning' : 'ok', detail: `${budgetUsage.filter((budget) => budget.state === 'crossed').length} exceeded` },
    { id: 'reports', label: 'Reports generated', state: 'info', detail: 'Generate Reports/PDF after review when needed' }
  ];

  return {
    monthKey,
    fromDate,
    toDate,
    status,
    income: sum(rangeTx.filter((row) => row.type === 'Income'), (row) => row.amount),
    expense: sum(rangeTx.filter((row) => row.type === 'Expense'), (row) => row.amount),
    investment: sum(rangeTx.filter((row) => row.type === 'Investment'), (row) => row.amount),
    netCashFlow: sum(rangeTx.filter((row) => row.type === 'Income'), (row) => row.amount) - sum(rangeTx.filter((row) => row.type === 'Expense'), (row) => row.amount) - sum(rangeTx.filter((row) => row.type === 'Investment'), (row) => row.amount),
    openingLiquidBalance: sum(accountRows, (row) => row.statement.openingBalance),
    closingLiquidBalance: sum(accountRows, (row) => row.statement.closingBalance),
    transferTotal: sum(rangeLedger.filter((row) => row.type === 'transfer_out'), (row) => row.amount),
    cardPaymentTotal: sum(rangeLedger.filter((row) => row.type === 'credit_card_payment' && row.direction === 'debit'), (row) => row.amount),
    closingCardOutstanding: sum(cardRows, (row) => row.closingOutstanding),
    duePlansTotal: sum(duePlans, (plan) => plan.amount),
    paidPlansTotal: sum(paidPlans, (payment) => payment.amount),
    overduePlansTotal: sum(overduePlans, (plan) => plan.amount),
    duePlans,
    paidPlans,
    unpaidPlans,
    overduePlans,
    pausedPlans,
    accountRows,
    cardRows,
    issues,
    criticalIssues,
    warnings,
    infoItems,
    checklist,
    budgetTotal,
    budgetSpent,
    budgetRemaining: budgetTotal - budgetSpent,
    budgetExceeded: budgetUsage.filter((budget) => budget.state === 'crossed').length,
    budgetWarning: budgetUsage.filter((budget) => budget.state === 'warning').length,
    crReceivable: sum(rangeCrdr.filter((row) => row.type === 'CR' && row.status !== 'Settled'), (row) => row.amount),
    drPayable: sum(rangeCrdr.filter((row) => row.type === 'DR' && row.status !== 'Settled'), (row) => row.amount),
    crdrSettled: sum(rangeCrdr.filter((row) => row.status === 'Settled'), (row) => row.amount),
    crdrOpenItems: rangeCrdr.filter((row) => row.status !== 'Settled').length
  };
}
